// @flow
// ----------------------------------------------------------------------
// <copyright file="Izenda.AreaChartOptionsBuilder.js" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
//  </copyright>
// ----------------------------------------------------------------------

import ChartHasThresholdOptionBuilder from './Izenda.ChartHasThresholdOptionBuilder';
import highchartUtils from 'share/components/charting/highcharts/Izenda.HighchartUtils';
import { RANGE_OPTION_KEYS } from 'logic/data/Izenda.ReportPartChartConstants';
import {
  get,
  isEmpty,
  flow,
  uniq,
  find,
  flatMap,
  assign,
  isNil,
  map,
  merge,
  forEach,
  concat,
  assignIn
} from 'lodash/fp';

import VISUAL_TYPE from 'share/components/charting/highcharts/Izenda.VisualTypeMapping';
import { exportClass } from 'share/Izenda.ServiceProvider';

@exportClass('AreaChartOptionsBuilder')
export default class AreaChartOptionsBuilder extends ChartHasThresholdOptionBuilder {
  constructor(...baseArgs) {
    super(...baseArgs);
  }

  /**
   * Build Highchart variable options for each chart type
   * @param {string} visualType Highchart chart type
   * @param {Object} highchartOptions Highchart options
   * @param {Object} chartData Chart data
   * @returns {Object}
   */
  buildOptionsByType(
    visualType: string,
    userOptions: any,
    dataParser: any
  ): any {
    let chartOptions = super.buildOptionsByType(
      visualType,
      userOptions,
      dataParser
    );

    const izendaOptions = userOptions.izendaOptions || {};
    const izendaSeriesConfig = userOptions.izendaSeriesConfig || {};
    const stacking = get('plotOptions.series.stacking', userOptions);
    const xAxisField = get('series[0].xAxisField', chartOptions);
    const yAxisField = get('series[0].yAxisField', chartOptions);
    const separatorField = get('series[0].separatorField', chartOptions);
    const series = chartOptions.series;
    const thresholdSeries = chartOptions.thresholdSeries;
    const areaSeries = [...series, ...thresholdSeries];

    const xAxisData = flow(
      get('data'),
      uniq
    )(dataParser.getDataByField(xAxisField));
    const groupNames = [];
    const groupedData = dataParser.groupData(separatorField, groupNames);

    const colors = isNil(userOptions.colors)
      ? this.DefaultColors
      : userOptions.colors;

    const isAreaChart = visualType === VISUAL_TYPE.Area;

    if (isAreaChart) {
      highchartUtils.setColorPointForAreaLine(userOptions, series, colors);
    }

    // For Area Chart with Stack & Separator, we need to add more point with yAxis Data = 0
    if (
      !!separatorField &&
      !!stacking &&
      !isEmpty(groupedData) &&
      isAreaChart
    ) {
      groupNames.forEach(groupName => {
        const groupRecords = groupedData[groupName];
        xAxisData.forEach(xAxisValue => {
          const existingPoint = find(
            record => record[xAxisField.columnName] === xAxisValue,
            groupRecords
          );
          if (!existingPoint) {
            const newRecord = {};
            newRecord[xAxisField.columnName] = xAxisValue;
            newRecord[yAxisField.columnName] = 0;
            newRecord[separatorField.columnName] = groupName;
            groupRecords.push(newRecord);
          }
        });
      });
      dataParser.setRecord(flatMap(data => data, groupedData));
      chartOptions =
        super.buildOptionsByType(visualType, userOptions, dataParser) || {};
    }

    chartOptions = assignIn(chartOptions, {
      series: areaSeries
    });

    /** [range only] option **/
    if (izendaOptions.range === RANGE_OPTION_KEYS.rangeOnly) {
      const seriesByRange = parserSeriesByRange(chartOptions.series);
      return {
        chart: {
          type: 'arearange'
        },
        series: seriesByRange,

        xAxis: chartOptions.xAxis,
        yAxis: assignIn(chartOptions.yAxis, {
          labels: {
            formatter() {
              return dataParser.getFormatData(
                this.value,
                izendaSeriesConfig[seriesByRange[0].name],
                [100]
              );
            }
          }
        }),

        tooltip: {
          formatter: getTooltipFormatByRange
        }
      };
    }

    /** [rangeWithAverage] option **/
    if (izendaOptions.range === RANGE_OPTION_KEYS.rangeWithAverage) {
      const seriesByRange = parserSeriesByRangeWithAverage(chartOptions.series);
      return {
        series: seriesByRange,

        xAxis: chartOptions.xAxis,
        yAxis: assignIn(chartOptions.yAxis, {
          labels: {
            formatter() {
              return dataParser.getFormatData(
                this.value,
                izendaSeriesConfig[seriesByRange[0].name],
                [100]
              );
            }
          }
        }),

        tooltip: {
          crosshairs: false,
          shared: true,
          formatter: getTooltipFormatByRangeWithAverage
        }
      };
    }

    /** [spline] option **/
    if (izendaOptions.spline) {
      return assignIn(
        {
          chart: {
            type: 'areaspline'
          }
        },
        chartOptions
      );
    }

    return chartOptions;

    function getTooltipFormatByRange() {
      const tooltip = [];
      const points = this.points || [this.point];
      const firstPoint = points[0] || {};
      const xAxis = chartOptions.xAxis || {};
      const formattedData = dataParser.getFormatData(
        firstPoint.key || firstPoint.name || firstPoint.x,
        izendaSeriesConfig[xAxis.axisName]
      );
      tooltip.push(`<b>${formattedData}</b><br/>`);

      // point
      points.forEach(serie => {
        const point = serie.point || {};
        const serieName = point.series.name;
        const low = dataParser.getFormatData(
          point.low || point.value,
          izendaSeriesConfig[serieName],
          point.series.yData
        );
        const high = dataParser.getFormatData(
          point.high || point.value,
          izendaSeriesConfig[serieName],
          point.series.yData
        );

        tooltip.push(
          `<span style="color:${
            point.color
          }">●</span>${serieName}: <b>${low} - ${high}</b><br/>`
        );
      });

      return tooltip.join('');
    }

    /**
     * Build tooltip for range with average.
     */
    function getTooltipFormatByRangeWithAverage() {
      const tooltip = [];
      const points = this.points || [this.point];
      const firstPoint = points[0] || {};
      const xAxis = chartOptions.xAxis || {};
      const tooltipColors = [];
      let tooltipColorIndex = 0;
      const formattedData = dataParser.getFormatData(
        firstPoint.key || firstPoint.name || firstPoint.x,
        izendaSeriesConfig[xAxis.axisName]
      );
      tooltip.push(`<b>${formattedData}</b><br/>`);

      // On line
      for (let i = 0; i < points.length; i++) {
        const point = points[i].point || {};
        if (!isNil(point.low)) continue;
        const serieName = point.series.name;
        const avg = dataParser.getFormatData(
          point.y || point.value,
          izendaSeriesConfig[serieName],
          point.series.yData
        );
        tooltipColors.push(point.color);

        tooltip.push(
          `<span style="color:${
            point.color
          }">●</span>${serieName}: <b>${avg}</b><br/>`
        );
      }

      // On range
      for (let j = 0; j < points.length; j++) {
        const point = points[j].point || {};
        if (isNil(point.low)) continue;
        const serieName = point.series.name;
        const low = dataParser.getFormatData(
          point.low || point.value,
          izendaSeriesConfig[serieName],
          point.series.yData
        );
        const high = dataParser.getFormatData(
          point.high || point.value,
          izendaSeriesConfig[serieName],
          point.series.yData
        );
        const tooltipColor = tooltipColors[tooltipColorIndex];
        tooltip.push(
          `<span style="color:${tooltipColor}">●</span>${serieName}: <b>${low} - ${high}</b><br/>`
        );
        tooltipColorIndex = tooltipColorIndex + 1;
      }

      return tooltip.join('');
    }

    function parserSeriesByRange(series: Array<any>) {
      series.forEach(serie => {
        serie.data = map(
          point =>
            merge(
              {
                low: point.min,
                high: point.max
              },
              point
            ),
          serie.data
        );
      });

      return series;
    }

    /**
     * Build series for range with average.
     * @param  {Array} series
     */
    function parserSeriesByRangeWithAverage(series: Array<any>) {
      let newSeries = [];

      forEach(serie => {
        newSeries = concat(buildChartData(serie), newSeries);
      }, series);

      return newSeries;

      function buildChartData(serie) {
        const lineSerie = assign(serie, {
          type: 'line',
          zIndex: 1,
          name: `${serie.name}_AVG`,
          data: []
        });

        const areaSerie = assign(serie, {
          type: 'arearange',
          lineWidth: 0,
          linkedTo: ':previous', // To link to the previous series.
          name: `${serie.name}_Range`,
          data: [],
          fillOpacity: 0.3,
          zIndex: 0
        });

        map(point => {
          lineSerie.data.push(
            merge(
              {
                y: point.avg
              },
              point
            )
          );

          areaSerie.data.push(
            merge(
              {
                low: point.min,
                high: point.max
              },
              point
            )
          );
        }, serie.data);

        return [areaSerie, lineSerie];
      }
    }
  }
}



// WEBPACK FOOTER //
// ./ui/modules/ReportDesigner/Fields/ReportBody/ReportPartType/ReportPartChart/Izenda.AreaChartOptionsBuilder.js